import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCloudArrowUp,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useRef, useState } from "react";

export interface SelectedFile {
  /** File name to display */
  name: string;
  /** Upload progress for this file (0-100). Omit if not uploading. */
  progress?: number;
}

export interface DSDropzoneProps {
  /** Required test ID for testing */
  dataTestId: string;
  /** Accepted file types (e.g., "image/*", ".pdf") */
  accept?: string;
  /** Allow multiple files */
  multiple?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Custom icon */
  icon?: IconProp;
  /** Main label */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Upload progress (0-100). Use for single overall progress. */
  uploadProgress?: number;
  /** Selected files to display. When provided, shows file list instead of upload prompt. */
  selectedFiles?: SelectedFile[];
  /** Callback when clear button is clicked */
  onClear?: () => void;
  /** Callback when files are selected */
  onFilesSelected?: (files: FileList) => void;
}

export const DSDropzone = ({
  dataTestId,
  accept,
  multiple = false,
  disabled = false,
  icon = faCloudArrowUp,
  label = "Upload a file",
  helperText = "Drag and drop or click to select",
  uploadProgress,
  selectedFiles,
  onClear,
  onFilesSelected,
}: DSDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const isUploading =
    uploadProgress !== undefined && uploadProgress < 100;
  const hasFiles = selectedFiles && selectedFiles.length > 0;

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!disabled && e.dataTransfer.files.length) {
      onFilesSelected?.(e.dataTransfer.files);
    }
  };

  const handleClick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      onFilesSelected?.(e.target.files);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.();
  };

  const stateStyles = disabled
    ? "border-disabled bg-disabled cursor-not-allowed opacity-50"
    : hasFiles
      ? "border-brand bg-secondary border-solid"
      : isDragging
        ? "border-brand bg-brand-subtle cursor-copy"
        : "border-secondary bg-secondary hover:border-brand hover:bg-brand-subtle";

  const borderStyle = hasFiles ? "" : "border-dashed";

  return (
    <div
      onClick={handleClick}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      className={`relative flex flex-col w-full items-center justify-center gap-sm p-lg rounded-standard border-2 transition-all cursor-pointer ${borderStyle} ${stateStyles}`}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
    >
      {hasFiles && onClear && (
        <button
          onClick={handleClear}
          className="absolute -top-2 -right-2 z-control bg-danger hover:bg-danger/80 text-white rounded-full w-6 h-6 flex items-center justify-center transition-colors"
          data-testid={`${dataTestId}-clear`}
        >
          <FontAwesomeIcon icon={faXmark} className="text-xs" />
        </button>
      )}

      {isUploading && !hasFiles ? (
        <>
          <div className="w-full max-w-2xl bg-tertiary rounded-full h-2 overflow-hidden">
            <div
              className="bg-brand h-full rounded-full transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <span className="text-sm text-secondary">
            Uploading... {uploadProgress}%
          </span>
        </>
      ) : hasFiles ? (
        <div className="w-full space-y-xs text-sm">
          {selectedFiles.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between gap-sm"
            >
              <span className="flex-1 max-w-96 overflow-hidden text-ellipsis whitespace-nowrap text-primary">
                {file.name}
              </span>
              {file.progress !== undefined && file.progress < 100 && (
                <div className="w-44 bg-tertiary rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-brand h-full rounded-full transition-all"
                    style={{ width: `${file.progress}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <>
          <FontAwesomeIcon
            icon={icon}
            className="text-3xl text-brand"
          />
          <span className="text-base font-semibold text-brand">
            {isDragging ? "Drop files here" : label}
          </span>
          <span className="text-sm text-secondary">{helperText}</span>
        </>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        disabled={disabled}
        className="sr-only"
        data-testid={`${dataTestId}-input`}
        data-intercom-target={`${dataTestId}-input`}
      />
    </div>
  );
};

DSDropzone.displayName = "DSDropzone";
